import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  getQuickDates,
  formatDateForInput,
  formatDateDisplay,
  addDaysToDate,
  parseUserDateInput,
} from '../utils/scheduleHelpers';

/**
 * Поле даты: нативный календарь (type="date"), вставка Ctrl+V (ДД.ММ.ГГГГ / Excel),
 * копирование Ctrl+C (ДД/ММ/ГГГГ) + быстрый выбор (⚡).
 * Сохранение: YYYY-MM-DD.
 */
const QuickDatePicker = ({
  value,
  onChange,
  onSave,
  onBlur,
  disabled = false,
  placeholder = '',
  relatedDate = null,
  isEndDate = false,
  relativeLabel = 'От даты начала',
}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  /** Пока фокус в поле — черновик из input, без лишнего formatDateForInput в value. */
  const [dateInputFocused, setDateInputFocused] = useState(false);
  const [dateInputDraft, setDateInputDraft] = useState('');
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  const quickDates = getQuickDates();

  const save = useCallback(
    (newValue) => {
      const normalized = newValue || null;
      if (onSave) {
        onSave(normalized);
      } else if (onBlur) {
        if (onChange) {
          onChange(normalized);
        }
        setTimeout(() => onBlur(), 0);
      }
    },
    [onSave, onBlur, onChange]
  );

  const applyDate = useCallback(
    (iso) => {
      const next = iso || '';
      setDateInputDraft(next);
      if (onChange) onChange(iso || null);
      save(iso || null);
    },
    [onChange, save]
  );

  const updatePopupPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopupPosition({
        top: rect.bottom + 4,
        left: Math.max(10, rect.left - 140),
      });
    }
  }, []);

  useEffect(() => {
    if (!showPopup) return;

    const handleClickOutside = (event) => {
      if (
        containerRef.current?.contains(event.target) ||
        popupRef.current?.contains(event.target)
      ) {
        return;
      }
      setShowPopup(false);
    };

    const handleScrollOrResize = () => updatePopupPosition();

    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 0);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside, true);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showPopup, updatePopupPosition]);

  const handleQuickDate = useCallback(
    (date) => {
      const iso = formatDateForInput(date);
      applyDate(iso);
      setShowPopup(false);
    },
    [applyDate]
  );

  const handleRelativeDate = useCallback(
    (days) => {
      const baseDate = relatedDate || value || quickDates.today;
      const newDate = addDaysToDate(baseDate, days);
      applyDate(newDate);
      setShowPopup(false);
    },
    [relatedDate, value, quickDates.today, applyDate]
  );

  const togglePopup = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      updatePopupPosition();
      setShowPopup((prev) => !prev);
    },
    [updatePopupPosition]
  );

  const handleInputChange = useCallback(
    (e) => {
      const newValue = e.target.value;
      setDateInputDraft(newValue);
      if (onChange) onChange(newValue || null);
    },
    [onChange]
  );

  const handleInputFocus = useCallback(() => {
    setDateInputFocused(true);
    setDateInputDraft(formatDateForInput(value));
  }, [value]);

  const handleInputBlur = useCallback(
    (e) => {
      const v = e.target.value;
      setDateInputFocused(false);
      save(v || null);
    },
    [save]
  );

  const handlePaste = useCallback(
    (e) => {
      const pasted = e.clipboardData?.getData('text/plain');
      if (!pasted?.trim()) return;

      const iso = parseUserDateInput(pasted);
      if (!iso) return;

      e.preventDefault();
      setDateInputFocused(true);
      applyDate(iso);
    },
    [applyDate]
  );

  const handleCopy = useCallback(
    (e) => {
      const text = formatDateDisplay(value);
      if (!text) return;
      e.preventDefault();
      e.clipboardData?.setData('text/plain', text);
    },
    [value]
  );

  const displayValue = dateInputFocused ? dateInputDraft : formatDateForInput(value);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <input
          ref={inputRef}
          type="date"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onPaste={handlePaste}
          onCopy={handleCopy}
          disabled={disabled}
          placeholder={placeholder}
          style={{ flex: 1, minWidth: '120px' }}
          title="Календарь, Ctrl+C — скопировать дату целиком (ДД/ММ/ГГГГ), Ctrl+V — вставка"
        />
        {!disabled && (
          <button
            ref={buttonRef}
            type="button"
            onClick={togglePopup}
            style={{
              padding: '4px 8px',
              border: '1px solid var(--border-color, #dee2e6)',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-secondary, #f8f9fa)',
              cursor: 'pointer',
              fontSize: '14px',
              lineHeight: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            title="Быстрый выбор даты"
          >
            ⚡
          </button>
        )}
      </div>

      {showPopup &&
        !disabled &&
        ReactDOM.createPortal(
          <div
            ref={popupRef}
            style={{
              position: 'fixed',
              top: popupPosition.top,
              left: popupPosition.left,
              zIndex: 'var(--z-dropdown)',
              backgroundColor: 'var(--bg-card, white)',
              border: '1px solid var(--border-color, #dee2e6)',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              padding: '10px',
              minWidth: '180px',
            }}
          >
            <div
              style={{
                fontSize: '12px',
                color: 'var(--text-muted, #6c757d)',
                marginBottom: '8px',
                fontWeight: 600,
                paddingBottom: '6px',
                borderBottom: '1px solid var(--border-color, #dee2e6)',
              }}
            >
              ⚡ Быстрый выбор
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <QuickDateButton
                onClick={() => handleQuickDate(quickDates.today)}
                label="Сегодня"
                icon="📅"
              />
              <QuickDateButton
                onClick={() => handleQuickDate(quickDates.tomorrow)}
                label="Завтра"
                icon="➡️"
              />
              <QuickDateButton
                onClick={() => handleQuickDate(quickDates.nextWeek)}
                label="Через неделю"
                icon="📆"
              />
              <QuickDateButton
                onClick={() => handleQuickDate(quickDates.nextMonth)}
                label="Через месяц"
                icon="🗓️"
              />
              <QuickDateButton
                onClick={() => handleQuickDate(quickDates.endOfQuarter)}
                label="Конец квартала"
                icon="📊"
              />
            </div>

            {relatedDate && (
              <>
                <div
                  style={{
                    borderTop: '1px solid var(--border-color, #dee2e6)',
                    marginTop: '8px',
                    paddingTop: '8px',
                    fontSize: '12px',
                    color: 'var(--text-muted, #6c757d)',
                    marginBottom: '6px',
                    fontWeight: 600,
                  }}
                >
                  ➕ {relativeLabel}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <QuickDateButton onClick={() => handleRelativeDate(0)} label="+0 дней" icon="➕" />
                  <QuickDateButton onClick={() => handleRelativeDate(7)} label="+7 дней" icon="➕" />
                  <QuickDateButton onClick={() => handleRelativeDate(14)} label="+14 дней" icon="➕" />
                  <QuickDateButton onClick={() => handleRelativeDate(30)} label="+30 дней" icon="➕" />
                  <QuickDateButton onClick={() => handleRelativeDate(60)} label="+60 дней" icon="➕" />
                  <QuickDateButton onClick={() => handleRelativeDate(90)} label="+90 дней" icon="➕" />
                </div>
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

const QuickDateButton = ({ onClick, label, icon }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClick();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 10px',
        border: 'none',
        borderRadius: '6px',
        backgroundColor: isHovered ? 'var(--table-hover, #e9ecef)' : 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        fontSize: '13px',
        color: 'var(--text-primary, #212529)',
        transition: 'background-color 0.15s ease',
        width: '100%',
      }}
    >
      <span style={{ fontSize: '14px', flexShrink: 0 }}>{icon}</span>
      <span style={{ flex: 1 }}>{label}</span>
    </button>
  );
};

export default QuickDatePicker;
